import {type ComponentType, useId, useState} from 'react'
import {useDetectedOS} from '@github-ui/swp-core/lib/hooks/use-detected-os'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import type {Icon} from '@primer/octicons-react'
import {RiverBreakoutTabs} from '@primer/react-brand/esm'
import {HeroTabsBackground} from './HeroTabsBackground'
import {HeroTabsCta} from './HeroTabsCta'
import {HeroTabsMedia} from './HeroTabsMedia'
import {pickHeroTabsMedia} from './hero-tabs-media'

import {
  findByIdWithSchema,
  findAllByContentType,
  findByContentType,
  GenericContentSchema,
  type GenericContent,
  type GenericGroup,
} from '../../../../../brand/lib/types/contentful'

import styles from './HeroTabs.module.css'

import CliDemo from './_demos/cli/Index'

// Maps the `demo` entry's htmlId in Contentful to its component.
const DEMOS: Partial<Record<string, ComponentType<{title?: string}>>> = {
  cli: CliDemo,
}

// A tab's `demo` entry, when its htmlId maps to a known demo component.
function getTabDemo(content: GenericContent['fields']['content']) {
  const demo = findByIdWithSchema(content, 'demo', GenericContentSchema)
  const Demo = demo?.fields.htmlId ? DEMOS[demo.fields.htmlId] : undefined
  return Demo ? {Demo, title: demo?.fields.heading} : undefined
}

type Props = {
  component: GenericGroup
  heading?: string
}

export function HeroTabs({component, heading}: Props) {
  const items = component.fields.content
  const os = useDetectedOS()
  const [selectedIndex, setSelectedIndex] = useState(0)

  // Tabs
  // RiverBreakoutTabs skips items without a heading or visual, so filter them out here
  // to keep `selectedIndex` aligned with the tabs that are actually rendered.
  const tabs = findAllByContentType(items, 'genericContent').filter(
    item =>
      item.fields.heading &&
      (getTabDemo(item.fields.content) || item.fields.media?.some(m => m.fields.asset.fields.file)),
  )

  // Background
  const background = findByContentType(items, 'genericAsset')

  // eslint-disable-next-line @eslint-react/naming-convention-id-name
  const descriptionIdPrefix = useId()
  const accessibleHeading = heading ?? tabs.at(0)?.fields.heading
  if (!accessibleHeading || tabs.length === 0) return null

  return (
    <RiverBreakoutTabs
      backgroundVisual={background ? <HeroTabsBackground component={background} /> : null}
      imagePosition="block-end"
      backgroundVisualFullBleed
      onChange={setSelectedIndex}
      className={styles.RiverBreakoutTabs}
    >
      <RiverBreakoutTabs.A11yHeading as="h2">{accessibleHeading}</RiverBreakoutTabs.A11yHeading>

      {tabs.map((item, index) => {
        const {label, heading: itemHeading, media, content} = item.fields
        const icon = getPrimerIcon(label?.fields.icon) as Icon
        const tabMedia = pickHeroTabsMedia(media, os)
        const demo = getTabDemo(content)

        return (
          <RiverBreakoutTabs.Item key={item.sys.id} className={styles.RiverBreakoutTabsItem}>
            {icon ? <RiverBreakoutTabs.Icon icon={icon} /> : null}
            {itemHeading ? <RiverBreakoutTabs.Heading as="h3">{itemHeading}</RiverBreakoutTabs.Heading> : null}

            <RiverBreakoutTabs.Content>
              <HeroTabsCta content={content} variant={index === selectedIndex ? 'primary' : 'secondary'} />
            </RiverBreakoutTabs.Content>

            {demo ? (
              <RiverBreakoutTabs.Visual className={styles.RiverBreakoutTabsVisual}>
                <demo.Demo title={demo.title} />
              </RiverBreakoutTabs.Visual>
            ) : tabMedia ? (
              <RiverBreakoutTabs.Visual className={styles.RiverBreakoutTabsVisual}>
                <HeroTabsMedia
                  media={tabMedia}
                  title={itemHeading}
                  descriptionId={`${descriptionIdPrefix}-${item.sys.id}`}
                  isSelected={index === selectedIndex}
                />
              </RiverBreakoutTabs.Visual>
            ) : null}
          </RiverBreakoutTabs.Item>
        )
      })}
    </RiverBreakoutTabs>
  )
}
